import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    Plus,
    Edit,
    Trash2,
    Eye,
    EyeOff,
    Calendar,
    Upload,
    X
} from "lucide-react";
import { Event, uploadEventBanner, deleteEventBanner } from "@/services/eventService";
import { GAME_GENRES } from "@/data/gameGenres";

export default function AdminEvents() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<Event | null>(null);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [genreSearch, setGenreSearch] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        page_slug: '',
        banner_image_url: '',
        wysiwyg_content: '',
        selected_genres: [] as string[],
        start_date: '',
        end_date: '',
        display_on_dashboard: false,
        is_active: true
    });

    useEffect(() => {
        if (user?.id) {
            checkAdminAccess();
        }
    }, [user?.id]);

    useEffect(() => {
        if (isAdmin) {
            fetchEvents();
        }
    }, [isAdmin]);

    const checkAdminAccess = async () => {
        if (!user) return;

        try {
            const { data } = await supabase
                .from("profiles")
                .select("is_admin, display_name")
                .eq("id", user.id)
                .single();

            if (data?.is_admin || data?.display_name === 'miikeu') {
                setIsAdmin(true);
            } else {
                toast.error("Access denied. Admin privileges required.");
                navigate("/dashboard");
            }
        } catch (error) {
            console.error("Error checking admin access:", error);
            toast.error("Error checking admin access");
            navigate("/dashboard");
        }
    };

    const fetchEvents = async () => {
        try {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setEvents((data || []) as Event[]);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (event?: Event) => {
        if (event) {
            setEditingEvent(event);
            setFormData({
                page_slug: event.page_slug,
                banner_image_url: event.banner_image_url || '',
                wysiwyg_content: event.wysiwyg_content || '',
                selected_genres: event.selected_genres || [],
                start_date: event.start_date ? event.start_date.split('T')[0] : '',
                end_date: event.end_date ? event.end_date.split('T')[0] : '',
                display_on_dashboard: event.display_on_dashboard,
                is_active: event.is_active
            });
            setBannerPreview(event.banner_image_url || null);
        } else {
            setEditingEvent(null);
            setFormData({
                page_slug: '',
                banner_image_url: '',
                wysiwyg_content: '',
                selected_genres: [],
                start_date: '',
                end_date: '',
                display_on_dashboard: false,
                is_active: true
            });
            setBannerPreview(null);
        }
        setBannerFile(null);
        setIsDialogOpen(true);
    };

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setBannerFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBannerPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleGenreToggle = (genre: string) => {
        setFormData(prev => ({
            ...prev,
            selected_genres: prev.selected_genres.includes(genre)
                ? prev.selected_genres.filter(g => g !== genre)
                : [...prev.selected_genres, genre]
        }));
    };

    const handleSave = async () => {
        if (!formData.page_slug.trim()) {
            toast.error('Page slug is required');
            return;
        }

        if (!formData.start_date || !formData.end_date) {
            toast.error('Start and end dates are required');
            return;
        }

        try {
            let bannerUrl = formData.banner_image_url;

            // Upload new banner if file selected
            if (bannerFile) {
                const uploadedUrl = await uploadEventBanner(bannerFile, formData.page_slug);
                if (uploadedUrl) {
                    bannerUrl = uploadedUrl;
                    // Delete old banner if updating
                    if (editingEvent?.banner_image_url && editingEvent.banner_image_url !== bannerUrl) {
                        await deleteEventBanner(editingEvent.banner_image_url);
                    }
                }
            }

            const eventData = {
                ...formData,
                banner_image_url: bannerUrl || null,
                created_by: user?.id
            };

            if (editingEvent) {
                const { error } = await supabase
                    .from('events')
                    .update({
                        ...eventData,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', editingEvent.id);

                if (error) throw error;
                toast.success('Event updated');
            } else {
                const { error } = await supabase
                    .from('events')
                    .insert([eventData]);

                if (error) throw error;
                toast.success('Event created');
            }

            setIsDialogOpen(false);
            fetchEvents();
        } catch (error: any) {
            console.error('Error saving event:', error);
            toast.error(error.message || 'Failed to save event');
        }
    };

    const handleDelete = async (id: string, bannerUrl: string | null) => {
        if (!confirm('Are you sure you want to delete this event?')) return;

        try {
            // Delete banner if exists
            if (bannerUrl) {
                await deleteEventBanner(bannerUrl);
            }

            const { error } = await supabase
                .from('events')
                .delete()
                .eq('id', id);

            if (error) throw error;
            toast.success('Event deleted');
            fetchEvents();
        } catch (error: any) {
            console.error('Error deleting event:', error);
            toast.error(error.message || 'Failed to delete event');
        }
    };

    const toggleActive = async (event: Event) => {
        try {
            const { error } = await supabase
                .from('events')
                .update({ is_active: !event.is_active, updated_at: new Date().toISOString() })
                .eq('id', event.id);

            if (error) throw error;
            fetchEvents();
            toast.success(`Event ${!event.is_active ? 'activated' : 'deactivated'}`);
        } catch (error: any) {
            console.error('Error toggling event:', error);
            toast.error('Failed to update event');
        }
    };

    if (!isAdmin || loading) {
        return (
            <AdminLayout>
                <div className="container mx-auto">
                    <div className="flex justify-center items-center min-h-[400px]">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="container mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold">Event Management</h1>
                        <p className="text-muted-foreground">Create and manage promotional events</p>
                    </div>
                    <Button onClick={() => handleOpenDialog()}>
                        <Plus className="w-4 h-4 mr-2" />
                        New Event
                    </Button>
                </div>

                {events.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground mb-4">No events created</p>
                            <Button onClick={() => handleOpenDialog()}>
                                <Plus className="w-4 h-4 mr-2" />
                                Create an event
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {events.map((event) => (
                            <Card key={event.id}>
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-semibold">/{event.page_slug}</h3>
                                                {event.is_active ? (
                                                    <Badge variant="outline" className="text-green-600 border-green-600">
                                                        <Eye className="w-3 h-3 mr-1" />
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-gray-600 border-gray-600">
                                                        <EyeOff className="w-3 h-3 mr-1" />
                                                        Inactive
                                                    </Badge>
                                                )}
                                                {event.display_on_dashboard && (
                                                    <Badge variant="outline">Dashboard</Badge>
                                                )}
                                            </div>
                                            {event.banner_image_url && (
                                                <img
                                                    src={event.banner_image_url}
                                                    alt="Event banner"
                                                    className="w-full max-w-md h-24 object-cover rounded mb-3"
                                                />
                                            )}
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(event.start_date).toLocaleDateString('en-US')} - {new Date(event.end_date).toLocaleDateString('en-US')}
                                                </div>
                                                <div>{event.selected_genres.length} genres selected</div>
                                            </div>
                                            {event.selected_genres.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {event.selected_genres.slice(0, 5).map((genre, idx) => (
                                                        <Badge key={idx} variant="secondary" className="text-xs">{genre}</Badge>
                                                    ))}
                                                    {event.selected_genres.length > 5 && (
                                                        <Badge variant="secondary" className="text-xs">+{event.selected_genres.length - 5}</Badge>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => toggleActive(event)}
                                            >
                                                {event.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleOpenDialog(event)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDelete(event.id, event.banner_image_url)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingEvent ? 'Edit Event' : 'New Event'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    />
                                    <Label htmlFor="is_active">Active</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="display_on_dashboard"
                                        checked={formData.display_on_dashboard}
                                        onCheckedChange={(checked) => setFormData({ ...formData, display_on_dashboard: checked })}
                                    />
                                    <Label htmlFor="display_on_dashboard">Display on Dashboard</Label>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="page_slug">Page Slug</Label>
                                <Input
                                    id="page_slug"
                                    value={formData.page_slug}
                                    onChange={(e) => setFormData({ ...formData, page_slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    placeholder="summer-sale"
                                />
                                <p className="text-xs text-muted-foreground mt-1">URL will be: /{formData.page_slug}</p>
                            </div>

                            <div>
                                <Label htmlFor="banner">Banner Image</Label>
                                <div className="mt-2">
                                    {bannerPreview && (
                                        <div className="relative mb-3">
                                            <img src={bannerPreview} alt="Banner preview" className="w-full h-48 object-cover rounded" />
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="absolute top-2 right-2"
                                                onClick={() => {
                                                    setBannerFile(null);
                                                    setBannerPreview(null);
                                                    setFormData({ ...formData, banner_image_url: '' });
                                                }}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                    <Input
                                        id="banner"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleBannerChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="start_date">Start Date</Label>
                                    <Input
                                        id="start_date"
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="end_date">End Date</Label>
                                    <Input
                                        id="end_date"
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>Game Genres ({formData.selected_genres.length} selected)</Label>
                                <Input
                                    placeholder="Search genres..."
                                    value={genreSearch}
                                    onChange={(e) => setGenreSearch(e.target.value)}
                                    className="mt-2 mb-2"
                                />
                                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                                    <div className="grid grid-cols-3 gap-2">
                                        {GAME_GENRES
                                            .filter(genre => genre.toLowerCase().includes(genreSearch.toLowerCase()))
                                            .map((genre) => (
                                                <div key={genre} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`genre-${genre}`}
                                                        checked={formData.selected_genres.includes(genre)}
                                                        onChange={() => handleGenreToggle(genre)}
                                                        className="rounded"
                                                    />
                                                    <label htmlFor={`genre-${genre}`} className="text-sm cursor-pointer">
                                                        {genre}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                    {GAME_GENRES.filter(genre => genre.toLowerCase().includes(genreSearch.toLowerCase())).length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-4">No genres found</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="content">Content (WYSIWYG - optional)</Label>
                                <RichTextEditor
                                    content={formData.wysiwyg_content}
                                    onChange={(content) => setFormData({ ...formData, wysiwyg_content: content })}
                                    placeholder="Enter event description..."
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSave}>
                                    {editingEvent ? 'Update' : 'Create'}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
}
